import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import RecyclingRoundedIcon from '@mui/icons-material/RecyclingRounded';
import PageLayout from '../components/PageLayout';
import Section, { SectionHeading } from '../components/PageSection';
import { FeatureCard, FeatureIcon, CardTitle, CardText, CheckItem, AccreditationNote } from '../components/ui';

const SITE = 'https://qasc.it';

// The two course families of the original /formazione/ambiente-e-qualita page.
const tcaSteps = [
  '1. Acquisizione della qualifica professionale',
  '2. Richiesta del riconoscimento abilitativo',
  '3. Termini del procedimento',
  '4. Iscrizione nell’elenco nominativo nazionale ENTECA',
];

const albo = [
  'Modulo propedeutico, obbligatorio per tutte le categorie — 18 ore',
  'Modulo di specializzazione cat. 1-4-5: raccolta e trasporto rifiuti — 18 ore',
  'Modulo di specializzazione cat. 8: intermediazione e commercio rifiuti — 18 ore',
  'Modulo di specializzazione cat. 9: bonifica siti — 18 ore',
  'Modulo di specializzazione cat. 10: bonifica di beni contenenti amianto — 18 ore',
];

function AmbienteQualitaPage() {
  return (
    <PageLayout
      seo={{
        title: 'Formazione Ambiente e Qualità | QAS Consulting',
        description:
          'Corsi di qualifica ambientale: aggiornamento per Tecnici Competenti in Acustica (TCA) riconosciuti ai sensi del D.Lgs. 42/2017 e corso per Responsabile tecnico Albo Gestori Ambientali.',
        canonical: `${SITE}/formazione/ambiente-e-qualita`,
      }}
      hero={{
        kicker: 'Formazione · Ambiente e Qualità',
        title: 'Formazione ambiente e qualità',
        subtitle: 'I corsi di qualifica e aggiornamento per le figure tecniche ambientali.',
      }}
    >
      {/* (1) TCA */}
      <Section tone="light">
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="flex-start">
            <Grid item xs={12} md={7}>
              <SectionHeading
                align="left"
                kicker="Formazione"
                title="Aggiornamento per Tecnici Competenti in Acustica (TCA)"
                sx={{ mb: 3 }}
              />
              <Typography sx={{ fontSize: '15.5px', lineHeight: 1.85, color: '#48606d', mb: 2 }}>
                Il Tecnico Competente in Acustica è la figura professionale idonea a effettuare le
                misurazioni, verificare l’ottemperanza ai valori definiti dalle vigenti norme,
                redigere i piani di risanamento acustico e svolgere le relative attività di
                controllo.
              </Typography>
              <Typography sx={{ fontSize: '15.5px', lineHeight: 1.85, color: '#48606d', mb: 2 }}>
                QAS Consulting offre incontri formativi su diversi temi, per far fronte
                all’obbligo di aggiornamento continuo previsto per i Tecnici Competenti in
                Acustica. I corsi sono riconosciuti e approvati ai sensi del D.Lgs. n. 42/2017 e
                del Decreto del CS ARPAV n. 175 del 03/06/2019, e vengono pubblicati sul sito
                ENTECA.
              </Typography>
              <Typography sx={{ fontSize: '15.5px', lineHeight: 1.85, color: '#48606d', mb: 2 }}>
                Si ricorda che il D.Lgs. 42/2017 richiede (allegato 1, punto 2) l’effettuazione in
                ogni quinquennio di almeno 30 ore di aggiornamento distribuite su almeno 3 anni.
              </Typography>
              <Typography
                sx={{ fontFamily: '"Sora", sans-serif', fontSize: '15px', fontWeight: 700, color: '#00425c' }}
              >
                Durata: da 4 a 6 ore per incontro
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <FeatureCard>
                <FeatureIcon accent="green">
                  <GraphicEqRoundedIcon />
                </FeatureIcon>
                <CardTitle>Come si diventa TCA</CardTitle>
                <CardText component="div">
                  <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
                    {tcaSteps.map((s) => (
                      <Box component="li" key={s} sx={{ py: '7px', borderBottom: '1px solid rgba(0,66,92,0.08)', '&:last-of-type': { borderBottom: 'none' } }}>
                        {s}
                      </Box>
                    ))}
                  </Box>
                </CardText>
              </FeatureCard>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* (2) Albo Gestori Ambientali */}
      <Section tone="mist">
        <Container maxWidth="lg">
          <SectionHeading
            kicker="Formazione"
            title="Corso per Responsabile tecnico Albo Gestori Ambientali"
            subtitle="Compito del Responsabile Tecnico è attuare azioni dirette ad assicurare la corretta organizzazione nella gestione dei rifiuti da parte dell’impresa nel rispetto della normativa vigente e vigilare sulla sua corretta applicazione. In base alle Delibere n. 6 e n. 7 del 30 maggio 2017, dal 16 ottobre 2017 i nuovi aspiranti responsabili tecnici — e coloro in carica che desiderano ricoprire l’incarico per nuove categorie o aumentare le classi di iscrizione — devono iscriversi e superare le verifiche previste."
          />
          <Typography
            sx={{ textAlign: 'center', fontSize: '15px', color: '#48606d', maxWidth: '760px', mx: 'auto', mb: 5 }}
          >
            Il corso affronta sia argomenti teorico-tecnici, sia aspetti operativi e procedurali,
            come richiesto dall’ampio ventaglio di responsabilità che competono a questa
            fondamentale figura professionale. La formazione si articola in:
          </Typography>
          <Grid container spacing={2.5}>
            {albo.map((item) => (
              <Grid item xs={12} sm={6} key={item}>
                <CheckItem>{item}</CheckItem>
              </Grid>
            ))}
          </Grid>
          <AccreditationNote />
          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Typography
              component={RouterLink}
              to="/contatti"
              sx={{
                fontFamily: '"Sora", sans-serif',
                fontWeight: 600,
                fontSize: '15.5px',
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Contattaci per creare un corso progettato su misura in base alle esigenze formative
              della tua azienda →
            </Typography>
          </Box>
        </Container>
      </Section>
    </PageLayout>
  );
}

export default AmbienteQualitaPage;
